import type { Plugin as SegmentPlugin } from '@segment/analytics-next';
import { postRequest } from './api-proxy-client';
import { validateEmail } from './utils/validate-email';

const IDENTITY_LINK_PATH = '/account/identity/link/';

// Matched here, not at the call sites, to cover the Embeddables and Digioh
// scripts that fire these through window.analytics.
export const IDENTITY_LINK_EVENTS = new Set([
  'Email Submitted',
  'Popup Form Submitted',
]);

// Digioh and Embeddables send the form fields their builders are configured
// with, so the email arrives under whatever casing the popup author used.
const EMAIL_PROPERTY_KEYS = new Set(['email', 'emailaddress', 'email_address']);

const findEmail = (
  properties?: Record<string, unknown>,
): string | undefined => {
  if (!properties) return undefined;
  const key = Object.keys(properties).find((name) =>
    EMAIL_PROPERTY_KEYS.has(name.toLowerCase()),
  );
  const value = key ? properties[key] : undefined;
  return typeof value === 'string' ? value : undefined;
};

// One POST per (email, anonymousId) per page session. CORE upserts the row, so
// this only trims repeat submits of the same address on the same device.
const linksPosted = new Set<string>();

/**
 * Records in CORE that `email` was submitted from this visitor's Segment
 * anonymousId. CORE replays the link as an identify at purchase, merging the
 * device's pre-purchase anonymous history into the buyer's profile.
 *
 * Failures are swallowed: this is a side effect of analytics, never a blocker
 * for the surface that collected the email.
 */
export const postIdentityLink = async (
  email: string,
  anonymousId: string,
): Promise<void> => {
  const trimmedEmail = email.trim();
  if (!validateEmail(trimmedEmail) || !anonymousId) return;

  const key = `${trimmedEmail.toLowerCase()}|${anonymousId}`;
  if (linksPosted.has(key)) return;
  linksPosted.add(key);

  try {
    await postRequest(
      IDENTITY_LINK_PATH,
      { email: trimmedEmail, anonymous_id: anonymousId },
      // The email step of an Embeddables flow often navigates straight after
      // submitting, and a dropped request loses the link for good.
      { keepalive: true },
    );
  } catch (error) {
    linksPosted.delete(key);
    console.error('Failed to link email to Segment anonymousId:', error);
  }
};

/**
 * Sends the submitted email and the visitor's anonymousId to CORE whenever one
 * of `IDENTITY_LINK_EVENTS` is tracked. Registered as an `after` plugin so it
 * runs once the event has reached Segment's destinations and never delays them.
 */
export const identityLinkPlugin: SegmentPlugin = {
  name: 'CORE Identity Link',
  version: '0.1.0',
  type: 'after',
  isLoaded: () => true,
  load: () => Promise.resolve(),
  track: (ctx) => {
    const { event, properties, anonymousId } = ctx.event;
    if (!event || !IDENTITY_LINK_EVENTS.has(event)) return ctx;

    const email = findEmail(properties);
    if (email && typeof anonymousId === 'string') {
      postIdentityLink(email, anonymousId);
    }
    return ctx;
  },
};
